import React, { useCallback, useState } from 'react';
import { FormattedMessage } from 'react-intl';

import {
  BtnBar,
  DangerAlert,
  Form,
  FormRow,
  SecondaryBtn,
  SuccessAlert,
  WaitingAlert,
} from '@tmapy/style-guide';

import { FormBlock } from '../../app/components/FormBlock';
import { QUERY_STATE } from '../../constants';
import { useFormItemServerErrors } from '../../hooks';
import type { ServerErrors } from '../../types';

import { ServiceOrderNoteField } from './ServiceOrderNoteField';
import { serviceOrderBtnMessages, serviceOrderFormMessages } from './serviceOrderMessages';

type ServiceOrderNoteFormProps = {
  note: string;
  submitQueryState: QUERY_STATE;
  serverErrors?: ServerErrors | null;
  onSubmit(values: { note: string }): void;
};

/** Potvrzená žádost má zmrazené ceny i strany; server v ní dovolí měnit jen poznámku. */
export const ServiceOrderNoteForm: React.FC<ServiceOrderNoteFormProps> = ({
  note: initialNote,
  submitQueryState,
  serverErrors,
  onSubmit,
}) => {
  const formItemServerErrors = useFormItemServerErrors(serverErrors);

  const [note, setNote] = useState(initialNote);
  const [isEditedSinceSubmit, setEditedSinceSubmit] = useState(false);

  const isSaving = submitQueryState === QUERY_STATE.WAITING;

  const handleNoteChange = useCallback((value: string) => {
    setEditedSinceSubmit(true);
    setNote(value);
  }, []);

  const handleSubmit = useCallback(() => onSubmit({ note: note.trim() }), [note, onSubmit]);

  return (
    <Form onSubmit={handleSubmit}>
      <FormBlock>
        <FormRow>
          <ServiceOrderNoteField
            value={note}
            isDisabled={isSaving}
            serverError={formItemServerErrors.note}
            onChange={handleNoteChange}
          />
        </FormRow>

        {formItemServerErrors.non_field_errors?.errorMessage && (
          <DangerAlert>{formItemServerErrors.non_field_errors.errorMessage}</DangerAlert>
        )}
        {isSaving && (
          <WaitingAlert>
            <FormattedMessage {...serviceOrderFormMessages.queryStateWaiting} />
          </WaitingAlert>
        )}
        {submitQueryState === QUERY_STATE.SUCCESS && !isEditedSinceSubmit && (
          <SuccessAlert>
            <FormattedMessage {...serviceOrderFormMessages.noteSubmitSuccess} />
          </SuccessAlert>
        )}
        {submitQueryState === QUERY_STATE.FAIL && !serverErrors && (
          <DangerAlert>
            <FormattedMessage {...serviceOrderFormMessages.submitFail} />
          </DangerAlert>
        )}

        <BtnBar>
          <SecondaryBtn type='submit' isDisabled={isSaving}>
            <FormattedMessage {...serviceOrderBtnMessages.saveNote} />
          </SecondaryBtn>
        </BtnBar>
      </FormBlock>
    </Form>
  );
};
